import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import useTranslation from '../hooks/useTranslation';

type PropsPagination = {
  page: number;
  pages: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPageChange: (value?: any) => void;
  maxNumber?: number;
}

export function Pagination({
  page = 1,
  pages,
  onPageChange: handleChangePage,
  maxNumber = 10
}: PropsPagination) {
  const { t } = useTranslation();

  const onPageChange = useCallback(
    (newPage) => {
      handleChangePage(newPage);
    },
    [handleChangePage]
  );

  const initialBreakPoint = useMemo(() => {
    const breackAt = (maxNumber / 2) - 1;
    return breackAt;
  }, [maxNumber]);

  const endBreakPoint = useMemo(() => {
    return pages - initialBreakPoint + 1;
  }, [initialBreakPoint, pages]);

  const isBreaked = useMemo(() => {
    return page > initialBreakPoint && page < endBreakPoint;
  }, [endBreakPoint, initialBreakPoint, page]);

  const initialRender = useMemo(() => {
    const inicio = 1;
    if (pages <= maxNumber) {
      return Array.from({ length: pages }, (_, index) => inicio + index);
    }
    if (!isBreaked) {
      const fin = initialBreakPoint;
      const numbers = Array.from({ length: fin }, (_, index) => inicio + index);
      return [...numbers, '...'];
    }
    const numbers = Array.from(
      { length: initialBreakPoint - initialBreakPoint / 2 },
      (_, index) => inicio + index
    );
    return [...numbers, '...'];
  }, [initialBreakPoint, isBreaked, maxNumber, pages]);

  const centerRender = useMemo(() => {
    if (
      pages <= maxNumber
      || page <= initialBreakPoint
      || page >= endBreakPoint
    ) {
      return [];
    }
    let inicio = page - 2;
    if (isBreaked) {
      const fin = initialBreakPoint;
      const numbers = Array.from({ length: fin }, (_, index) => inicio + index);
      const finalPage = inicio + fin;
      if (finalPage < pages) {
        return [...numbers, '...'];
      }
      return [...numbers];
    }
    inicio = page - 2;
    const fin = initialBreakPoint;
    const finalPage = inicio + fin;
    const numbers = Array.from({ length: fin }, (_, index) => inicio + index);
    if (finalPage < pages) {
      return [...numbers, '...'];
    }
    return [...numbers];
  }, [endBreakPoint, initialBreakPoint, isBreaked, maxNumber, page, pages]);

  const endRender = useMemo(() => {
    if (pages <= maxNumber) {
      return [];
    }
    if (isBreaked) {
      const fin = initialBreakPoint - 3;
      const inicio = pages - fin + 1;
      const numbers = Array.from({ length: fin }, (_, index) => inicio + index);
      return numbers;
    }
    const inicio = pages - initialBreakPoint + 1;
    const numbers = Array.from(
      { length: initialBreakPoint },
      (_, index) => inicio + index
    );
    return numbers;
  }, [initialBreakPoint, isBreaked, maxNumber, pages]);

  function renderPart(part) {
    return (
      <>
        {part.map((pageToSelect) => {
          return (
            <li
              className={classNames('page-item', {
                active: pageToSelect === page,
                disabled: pageToSelect === '...'
              })}
              key={pageToSelect}
            >
              <button
                className="page-link"
                type="button"
                disabled={pageToSelect === '...'}
                onClick={() => onPageChange(pageToSelect)}
              >
                {pageToSelect}
              </button>
            </li>
          );
        })}
      </>
    );
  }

  return (
    <nav aria-label={t('Page navigation')}>
      <ul className="pagination">
        <li
          className={classNames('page-item', {
            disabled: page <= 1
          })}
        >
          <button
            className="page-link"
            type="button"
            aria-label={t('Previous')}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <span aria-hidden="true">&laquo;</span>
          </button>
        </li>
        {renderPart(initialRender)}
        {renderPart(centerRender)}
        {renderPart(endRender)}
        <li
          className={classNames('page-item', {
            disabled: page >= pages
          })}
        >
          <button
            className="page-link"
            type="button"
            aria-label={t('Next')}
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
          >
            <span aria-hidden="true">&raquo;</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default Pagination;
